import { useState } from 'react'
import { FileWithPath } from 'react-dropzone'

import { chunk, groupBy, isNaN, orderBy, uniq } from 'lodash'

import qs from 'qs'

import { parse, ParseResult } from 'papaparse'
import moment from 'moment'

import { useDispatch, useSelector } from '@tabeeb/store'
import {
  Body2,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DragAndDropFiles,
  FormHelperText,
  Link2,
  Stack,
} from '@collaborate-health/uikit'
import { getShowTimeZoneOnUploadAnswers } from '@tabeeb/modules/appConfigState/selectors'
import TimeZonePicker from '@collaborate-health/uikit/components/TimeZonePicker'

import { getCurrentVideoTimestamp } from '@tabeeb/modules/player/selectors'
import { useApi } from '@tabeeb/modules/shared/utils/API/hooks'
import { useFormTitleFormatter } from '@tabeeb/modules/shared/utils/text'
import { ControlType, CreateFormAnswerRequest } from '@tabeeb/contracts/main-api/swagger-main-api'
import { getContentId } from '@tabeeb/modules/shared/content/selectors'

import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'
import { updatePageNameRequest } from '@tabeeb/modules/gallery/actions'
import { getCurrentUserId } from '@tabeeb/modules/account/selectors'
import { FormApi, IAnswer } from '@collaborate-health/forms'
import { getCurrentForm, getSelectedGalleryItemId } from '../../selectors'
import { mapFormControlAnswerRequestToFormAnswer, mapFormControlAnswerToFormAnswer } from '../../mapping'

const FormAnswersUploadFileExampleUrl = new URL(
  '@tabeeb/assets/docs/uploadformanswerstemplate.csv',
  import.meta.url
).toString()

const containsTimeZone = (dateString: string) => {
  const lower = dateString.toLowerCase()

  return lower.endsWith('z') || /([+-]\d{2}:\d{2})$/.test(lower)
}

export interface UploadFormAnswersDialogProps {
  open: boolean
  formRef: React.RefObject<FormApi>
  onClose: () => void
}

const UploadFormAnswersDialog: React.FC<UploadFormAnswersDialogProps> = ({ open, formRef: formApiRef, onClose }) => {
  const dispatch = useDispatch()

  const [selectedFile, setSelectedFile] = useState<FileWithPath>()
  const [selectedTimeZone, setSelectedTimeZone] = useState(moment.tz.guess())
  const showTimeZoneDropdown = useSelector(getShowTimeZoneOnUploadAnswers)

  const [errors, setErrors] = useState<string[]>([])

  const { mainApi: api } = useApi()

  const currentUserId = useSelector(getCurrentUserId)
  const currentForm = useSelector(getCurrentForm)
  const timestamp = useSelector(getCurrentVideoTimestamp)
  const currentPageId = useSelector(getSelectedGalleryItemId)
  const contentId = useSelector(getContentId)
  const timeZoneOffsetInMinutes = showTimeZoneDropdown ? -moment.tz(selectedTimeZone).utcOffset() : 0

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = () => {
    setIsSubmitting(true)

    parse(selectedFile!, {
      header: true,
      skipEmptyLines: true,
      complete: async (result: ParseResult<{ 'Tabs numbers': string; 'Question number': string; Answer: string }>) => {
        const answersErrors: string[] = []

        if (!result.meta.fields) {
          answersErrors.push('CSV header is missing')
        } else {
          if (!result.meta.fields?.includes('Tabs numbers')) {
            answersErrors.push('Tabs numbers column is missing')
          }

          if (!result.meta.fields?.includes('Question number')) {
            answersErrors.push('Question number column is missing')
          }

          if (!result.meta.fields?.includes('Answer')) {
            answersErrors.push('Answer column is missing')
          }
        }

        if (result.data.length === 0) {
          answersErrors.push('No data found in the file')
        }

        const formTabsPanels = orderBy(currentForm.Tabspanels, (p) => p.Order)
        const controls = orderBy(currentForm.Controls, (c) => c.Order)
        const timezone = moment.tz.guess()

        const answers: CreateFormAnswerRequest[] = []

        for (let rowIndex = 0; rowIndex < result.data.length; rowIndex++) {
          const row = result.data[rowIndex]

          const controlIndex = parseInt(row['Question number'], 10) - 1
          if (isNaN(controlIndex) || controlIndex < 0) {
            answersErrors.push(`Question number is invalid`)
            continue
          }

          const control = controls[controlIndex]
          if (!control) {
            answersErrors.push(`No question found with number ${row['Question number']}`)
            continue
          }

          const value = row.Answer?.trim()
          if (!value) {
            continue
          }

          const answer: CreateFormAnswerRequest = {
            ContentId: contentId,
            ContentFormId: currentForm.Id!,
            PageId: currentPageId,
            FormControlId: control.Id,
            Timestamp: timestamp,
            TimeZone: timezone,
            OptionsIds: [],
            FormTabsIds: [],
            Value: value,
          }

          if (currentForm.Tabspanels?.length) {
            const formTabsIds =
              row['Tabs numbers']
                ?.split('_')
                ?.map((n) => parseInt(n, 10) - 1)
                ?.map((tabIndex, tabPanelIndex) => formTabsPanels[tabPanelIndex]?.Tabs![tabIndex]?.Id)
                .filter((n) => !isNaN(n)) ?? []

            if (formTabsIds.length !== formTabsPanels.length) {
              answersErrors.push(`Wrong tabs combination`)

              continue
            }

            answer.FormTabsIds = formTabsIds
          }

          if (control.Options?.length) {
            const optionValues = value?.split('_').map((v) => v.trim()) ?? []

            const selectedOptionsIds: number[] = []

            for (const optionValue of optionValues) {
              const option = control.Options?.find((o) => o.Value === optionValue)
              if (!option) {
                answersErrors.push(`Option value "${optionValue}" is invalid for question "${control.Label}"`)

                continue
              }

              selectedOptionsIds.push(option.Id)
            }

            answer.OptionsIds = uniq(selectedOptionsIds)
          }

          if (value && [ControlType.Date, ControlType.DateTime].includes(control.ControlType)) {
            const date = moment(value)
            if (!date.isValid()) {
              answersErrors.push(`Incorrect date format "${value}" for question "${control.Label}"`)

              continue
            }

            if (containsTimeZone(value)) {
              answer.Value = date.toISOString()
            } else {
              answer.Value = date.add(timeZoneOffsetInMinutes, 'minutes').utc(true).toISOString()
            }
          }

          answers.push(answer)
        }

        if (answersErrors.length) {
          setErrors(answersErrors)
          setIsSubmitting(false)

          return
        }

        try {
          const groupedByTabs = groupBy(answers, (a) => a.FormTabsIds?.join('_'))
          for (const group of Object.values(groupedByTabs)) {
            const tabs = group[0].FormTabsIds ?? []
            const tabNames = formTabsPanels
              .map((tp, i) => tp.Tabs?.find((t) => t.Id === tabs[i])?.Value ?? '')
              .filter((n) => n)
              .join(' / ')

            // eslint-disable-next-line no-await-in-loop
            const existingAnswers = await api.formAnswers.getAnswersByTabIdAsync(
              {
                contentFormId: currentForm.ContentFormId!,
                userId: currentUserId,
                pageId: currentPageId,
                formTabs: tabs,
              },
              {
                paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
              }
            )

            const tabsAnswers: IAnswer[] = []

            for (const newAnswer of group) {
              const control = controls.find((c) => c.Id === newAnswer.FormControlId)
              if (!control) {
                continue
              }

              const formAnswer = mapFormControlAnswerRequestToFormAnswer(newAnswer, control)

              tabsAnswers.push(formAnswer!)
            }

            for (const existingAnswer of existingAnswers) {
              if (tabsAnswers.some((a) => a.controlId === existingAnswer.FormControlId.toString())) {
                continue
              }

              const control = controls.find((c) => c.Id === existingAnswer.FormControlId)
              if (!control) {
                continue
              }

              const formAnswer = mapFormControlAnswerToFormAnswer(existingAnswer, control)
              if (!formAnswer) {
                continue
              }

              tabsAnswers.push(formAnswer)
            }

            // eslint-disable-next-line no-await-in-loop
            const { isValid, errors: validationErrors } = await formApiRef.current!.validateAnswersAsync(tabsAnswers)
            if (!isValid) {
              for (const error of validationErrors) {
                let errorMessage = `${error.control.name}: ${error.message}`
                if (tabNames) {
                  errorMessage = `(${tabNames}) - ${errorMessage}`
                }

                answersErrors.push(errorMessage)
              }
            }
          }

          if (answersErrors.length) {
            setErrors(answersErrors)

            return
          }

          for (const answersChunk of chunk(answers, 10)) {
            // eslint-disable-next-line no-await-in-loop
            await Promise.all(answersChunk.map((answer) => api.formAnswers.postAsync(answer)))
          }

          dispatch(updatePageNameRequest({ PageId: currentPageId, ContentId: contentId }))
          dispatch(onAddSuccessNotification({ message: f(`Answers added to the form successfully`) }))

          onClose()
        } catch {
          dispatch(onAddErrorNotification({ message: f('Failed to upload form answers') }))
        } finally {
          setIsSubmitting(false)
        }
      },
    })
  }

  const { f } = useFormTitleFormatter()

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth='sm'
      onClose={onClose}
      TransitionProps={{
        onExited: () => {
          setSelectedFile(undefined)
          setErrors([])
        },
      }}
    >
      <DialogTitle>{f('Upload form answers')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DragAndDropFiles
            file={selectedFile!}
            disabled={isSubmitting}
            acceptMimeFormats={{
              'text/csv': ['.csv'],
            }}
            acceptedFormatDisplayStrings={['csv']}
            maximumSizeOfMegabytes={10}
            placeholder='Please select file to upload'
            fileUploadButtonText='Select file'
            onDropAccepted={(droppedFiles) => {
              setErrors([])
              setSelectedFile(droppedFiles[0])
            }}
            onClearState={() => {
              setErrors([])
              setSelectedFile(undefined)
            }}
          />

          {showTimeZoneDropdown && (
            <TimeZonePicker selectedTimeZone={selectedTimeZone} onTimeZoneChange={(tz) => setSelectedTimeZone(tz)} />
          )}

          {errors.length > 0 && (
            <Stack spacing={1}>
              {errors.map((error) => (
                <FormHelperText key={error} error>
                  {error}
                </FormHelperText>
              ))}
            </Stack>
          )}

          <Body2>
            {`Don't have the CSV for uploading? `}
            <Link2 href={FormAnswersUploadFileExampleUrl}>Click here to download the template</Link2>
          </Body2>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color='primary' onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          loading={isSubmitting}
          onClick={handleSubmit}
          color='primary'
          variant='contained'
          disabled={!selectedFile || (!selectedTimeZone && showTimeZoneDropdown)}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UploadFormAnswersDialog

